import type { AssetClientContext, AssetDelivery } from "@pej/asset-client";
import type { QueryClient } from "@tanstack/react-query";
import type { TemplateBody, TemplateSection } from "@/api/types";
import { pickArtworkUrl, pickFontUrl } from "./asset-projections";
import { manifestQuery } from "./asset-queries";

/**
 * Turns the artwork references a save is about to write into the public render
 * URLs stored beside them.
 *
 * This exists because the display page is public — capability token, no login —
 * so its endpoint has no session to read a delivery manifest with, while the
 * manifest endpoint is workspace-authenticated. The operator's session, here,
 * is the only one that can make the translation, so the admin makes it once at
 * save time and persists the answer. See the core schema; when the backend gets
 * a credential of its own this becomes redundant and the server resolves again.
 *
 * Runs at submit rather than reading whatever the preview last rendered: the
 * preview's URL is a render-time value that can lag the form by a fetch, and
 * writing it would pair a newly picked asset with the previous one's artwork.
 * Everything here shares the manifest cache the preview and the picker already
 * use, so the common case costs no request at all.
 *
 * The uploaded font is a column too (`fontFaceUrl` beside `fontAssetId`) and
 * follows `backgroundImage`'s rule exactly; only the URL it picks differs — a
 * font's is the original file, since fonts get no renditions.
 *
 * `backgroundImage` and `sections` are resolved by different rules on purpose.
 * `backgroundImage` is a column: the `changed` flag says whether this save is
 * the one moving the reference, and an unchanged, unresolvable slot can leave
 * the stored column alone. `sections` is a JSONB array replaced wholesale on
 * every save — there is no per-item "leave this one alone" to express — so
 * every image section resolves every time, changed or not.
 */
export interface ArtworkField {
    assetId: string | null;
    /** Whether this save changes which asset is referenced. */
    changed: boolean;
}

export type ArtworkUrls = Pick<TemplateBody, "backgroundImageUrl" | "fontFaceUrl" | "sections">;

type PickUrl = (manifest: AssetDelivery) => string | null;

/** A value to write, or nothing at all — "leave the stored column alone". */
type Resolution = { write: true; url: string | null } | { write: false };

export async function resolveArtworkUrls(
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
    fields: { backgroundImage: ArtworkField; font: ArtworkField; sections: TemplateSection[] },
): Promise<ArtworkUrls> {
    const [backgroundImage, font, sections] = await Promise.all([
        resolveOne(ctx, queryClient, fields.backgroundImage, pickArtworkUrl),
        resolveOne(ctx, queryClient, fields.font, pickFontUrl),
        Promise.all(fields.sections.map((section) => resolveSection(ctx, queryClient, section))),
    ]);
    return {
        ...(backgroundImage.write ? { backgroundImageUrl: backgroundImage.url } : {}),
        ...(font.write ? { fontFaceUrl: font.url } : {}),
        sections,
    };
}

async function resolveOne(
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
    field: ArtworkField,
    pick: PickUrl,
): Promise<Resolution> {
    // Nothing referenced. Clear the stored URL if this save is what removed
    // the reference; if the slot was simply never filled, say nothing at all
    // rather than putting a null in every body.
    if (!field.assetId) return field.changed ? { write: true, url: null } : { write: false };

    const url = ctx ? await manifestUrl(ctx, queryClient, field.assetId, pick) : null;
    if (url) return { write: true, url };

    // Unresolvable — no asset service, a deleted asset, or the service being
    // briefly unreachable. What to do depends entirely on whether this save
    // is also moving the reference:
    //
    // - It is: the stored URL belongs to the *previous* asset, so keeping it
    //   would put the old artwork on screens under the new asset's id. Clear
    //   it and let the screen degrade to no artwork, as it does for every
    //   other asset failure in this product.
    // - It is not: the body is a patch, so omitting the key leaves the column
    //   untouched. A blip while someone edits the footer must not wipe artwork
    //   that is rendering correctly right now.
    return field.changed ? { write: true, url: null } : { write: false };
}

/**
 * Resolves one section's artwork, or passes the section through untouched.
 *
 * Every image section is resolved here, whether or not this save is the one
 * that picked its asset — see the module doc for why that differs from
 * `backgroundImage`. `assetUrl: null` on the result means UNRESOLVED, never
 * "no artwork": the display page falls back to resolving from `assetId`
 * itself, and only shows no artwork if that also fails.
 */
async function resolveSection(
    ctx: AssetClientContext | null,
    queryClient: QueryClient,
    section: TemplateSection,
): Promise<TemplateSection> {
    if (section.type !== "image" || !section.assetId) return section;
    const url = ctx ? await manifestUrl(ctx, queryClient, section.assetId, pickArtworkUrl) : null;
    return { ...section, assetUrl: url };
}

async function manifestUrl(ctx: AssetClientContext, queryClient: QueryClient, assetId: string, pick: PickUrl) {
    try {
        return pick(await queryClient.fetchQuery(manifestQuery(ctx, assetId)));
    } catch {
        // A failed resolution is never fatal to the save — the caller decides
        // what a missing URL means for this particular field.
        return null;
    }
}
